import axios from "axios";
import { createContext, useContext, useEffect, useState, useCallback } from "react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// ============ Axios instance with JWT interceptor ============
export const api = axios.create({
    baseURL: API,
    headers: { "Content-Type": "application/json" },
});

const STORAGE_KEY = "cursus_physio_token";

export function getStoredToken() {
    try {
        return localStorage.getItem(STORAGE_KEY);
    } catch {
        return null;
    }
}

export function setStoredToken(token) {
    if (token) localStorage.setItem(STORAGE_KEY, token);
    else localStorage.removeItem(STORAGE_KEY);
}

api.interceptors.request.use((config) => {
    const token = getStoredToken();
    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
});

api.interceptors.response.use(
    (resp) => resp,
    (error) => {
        if (error?.response?.status === 401) {
            // token expired or invalid -> clear
            setStoredToken(null);
            // don't force redirect here (Login page uses this too); components decide
        }
        return Promise.reject(error);
    }
);

// Utility: format FastAPI error detail safely (detail can be string or array of objects)
export function formatApiError(err) {
    const d = err?.response?.data?.detail;
    if (d == null) return err?.message || "Une erreur est survenue.";
    if (typeof d === "string") return d;
    if (Array.isArray(d)) {
        return d.map((e) => (typeof e?.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
    }
    if (typeof d?.msg === "string") return d.msg;
    return String(d);
}

export const LOGO_URL =
    "https://customer-assets.emergentagent.com/job_kinesi-cursus/artifacts/nz65me0n_logo_Cursus_Physio.png";

// ============ AuthContext ============

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
    const [user, setUser] = useState(undefined); // undefined = loading, null = not authed
    const [loading, setLoading] = useState(true);

    const refresh = useCallback(async () => {
        const token = getStoredToken();
        if (!token) {
            setUser(null);
            setLoading(false);
            return null;
        }
        try {
            const { data } = await api.get("/auth/me");
            setUser(data);
            setLoading(false);
            return data;
        } catch {
            setStoredToken(null);
            setUser(null);
            setLoading(false);
            return null;
        }
    }, []);

    useEffect(() => {
        refresh();
    }, [refresh]);

    const login = useCallback(async (email, password) => {
        const { data } = await api.post("/auth/login", { email, password });
        setStoredToken(data.access_token);
        setUser(data.user);
        return data.user;
    }, []);

    const logout = useCallback(async () => {
        try {
            await api.post("/auth/logout");
        } catch {
            // ignore — we still clear local state
        }
        setStoredToken(null);
        setUser(null);
    }, []);

    return (
        <AuthContext.Provider value={{ user, loading, login, logout, refresh, setUser }}>
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    const ctx = useContext(AuthContext);
    if (!ctx) throw new Error("useAuth must be used within an AuthProvider");
    return ctx;
}
